import React, { useState, useEffect } from 'react';
import { DEFAULT_WALL_OF_FAME } from '../data/wallOfFame';
import { resolveMedia, isVideoUrl } from '../lib/media';
import { createPortal } from 'react-dom';
import { ZoomIn, X, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from '../components/ui/dialog';
import EditionsHistoryModal from '../components/EditionsHistoryModal';

// Icons SVG
const SpotifyIcon = ({ className }) => (
  <svg viewBox="0 0 168 168" className={className} aria-hidden="true">
    <path d="M83.996.277C37.747.277.253 37.77.253 84.019c0 46.251 37.494 83.741 83.743 83.741 46.254 0 83.744-37.49 83.744-83.741 0-46.246-37.49-83.738-83.745-83.738zm38.404 120.78a5.217 5.217 0 01-7.18 1.73c-19.662-12.01-44.414-14.73-73.564-8.07a5.222 5.222 0 01-6.249-3.93 5.213 5.213 0 013.926-6.25c31.9-7.29 59.263-4.15 81.337 9.34 2.46 1.51 3.24 4.72 1.73 7.18zm10.25-22.805c-1.89 3.075-5.91 4.045-8.98 2.155-22.51-13.839-56.823-17.846-83.448-9.764-3.453 1.043-7.1-.903-8.148-4.35-1.04-3.453.907-7.093 4.354-8.143 30.413-9.228 68.222-4.758 94.072 11.127 3.07 1.89 4.04 5.91 2.15 8.976v-.001zm.88-23.744c-26.99-16.031-71.52-17.505-97.289-9.684-4.138 1.255-8.514-1.081-9.768-5.219a7.835 7.835 0 015.221-9.771c29.581-8.98 78.756-7.245 109.83 11.202a7.833 7.833 0 012.74 10.733c-2.2 3.722-7.02 4.949-10.73 2.739z"/>
  </svg>
);

const YouTubeIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
  </svg>
);

// Detect media type from URL
const getMediaType = (url) => {
  if (!url) return null;
  if (url.includes('youtu.be') || url.includes('youtube.com')) return 'youtube';
  if (url.includes('instagram.com')) return 'instagram';
  return 'spotify';
};

// Converte URL pubblica nell'URL embed della piattaforma corrispondente
const toEmbedUrl = (url) => {
  if (!url) return null;
  // YouTube - formato youtu.be/ID
  const ytShort = url.match(/youtu\.be\/([A-Za-z0-9_-]+)/);
  if (ytShort) return `https://www.youtube.com/embed/${ytShort[1]}?autoplay=1&rel=0`;
  // YouTube - formato youtube.com/watch?v=ID
  if (url.includes('youtube.com')) {
    const ytLong = url.match(/[?&]v=([A-Za-z0-9_-]+)/);
    if (ytLong) return `https://www.youtube.com/embed/${ytLong[1]}?autoplay=1&rel=0`;
  }
  // Instagram - /p/, /reel/, /tv/
  const igMatch = url.match(/instagram\.com\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
  if (igMatch) return `https://www.instagram.com/${igMatch[1]}/${igMatch[2]}/embed`;
  // Spotify - track/artist/album/playlist/episode/show (con supporto intl-xx)
  const spotifyMatch = url.match(
    /open\.spotify\.com\/(?:intl-[a-z]{2}\/)?(track|artist|album|playlist|episode|show)\/([A-Za-z0-9]+)/
  );
  if (spotifyMatch) return `https://open.spotify.com/embed/${spotifyMatch[1]}/${spotifyMatch[2]}`;
  // Non embeddabile (es. pagine di ricerca)
  return null;
};

const MEDIA_CONFIG = {
  spotify: {
    Icon: SpotifyIcon,
    badgeBg: 'bg-[#1DB954] hover:bg-[#1ed760]',
    buttonBg: 'bg-[#1DB954] hover:bg-[#1ed760]',
    label: 'ASCOLTA SU SPOTIFY',
    title: 'Ascolta su Spotify',
  },
  youtube: {
    Icon: YouTubeIcon,
    badgeBg: 'bg-[#FF0000] hover:bg-[#ff2b2b]',
    buttonBg: 'bg-[#FF0000] hover:bg-[#ff2b2b]',
    label: 'GUARDA SU YOUTUBE',
    title: 'Guarda su YouTube',
  },
  instagram: {
    Icon: InstagramIcon,
    badgeBg: 'bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:opacity-90',
    buttonBg: 'bg-gradient-to-tr from-[#feda75] via-[#fa7e1e] via-40% to-[#d62976] hover:opacity-90',
    label: 'GUARDA SU INSTAGRAM',
    title: 'Guarda su Instagram',
  },
};

const DEFAULT_ITEMS = DEFAULT_WALL_OF_FAME;

const isVideoSrc = (img) => img.isVideo || isVideoUrl(img.src);

const GalleryMedia = ({ img, className }) => isVideoSrc(img) ? (
  <video src={resolveMedia(img.src)} className={className} autoPlay muted loop playsInline preload="auto" data-testid={`gallery-video-${img.id}`} />
) : (
  <img src={resolveMedia(img.src)} alt={img.caption} className={className} />
);

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [playerMedia, setPlayerMedia] = useState(null); // { url, embedUrl, type, caption, year }
  const [galleryImages, setGalleryImages] = useState(DEFAULT_ITEMS);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/wall-of-fame`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.items) && d.items.length) setGalleryImages(d.items); })
      .catch(() => {});
  }, []);

  const openPlayer = (img, mediaType) => {
    const embedUrl = toEmbedUrl(img.spotifyUrl);
    if (!embedUrl) {
      // Fallback: apertura esterna solo se non embeddabile
      window.open(img.spotifyUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setPlayerMedia({
      url: img.spotifyUrl,
      embedUrl,
      type: mediaType,
      caption: img.caption,
      year: img.year,
    });
  };

  // Dimensioni adattive per tipo di player
  const playerSizeClass = (type) => {
    if (type === 'instagram') return 'max-w-md'; // IG ha aspetto verticale
    if (type === 'spotify') return 'max-w-xl';
    return 'max-w-3xl'; // YouTube
  };

  const playerAspect = (type) => {
    if (type === 'instagram') return { paddingBottom: '125%' }; // ~4:5
    if (type === 'spotify') return { paddingBottom: '40%' };
    return { paddingBottom: '56.25%' }; // 16:9
  };

  return (
    <div className="py-10 sm:py-16 px-4" style={{ backgroundColor: 'var(--tp-black)', minHeight: '80vh' }}>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-2 text-white"
          style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}>
          WALL OF FAME
        </h1>
        <div className="w-20 h-1 mx-auto mb-8" style={{ backgroundColor: 'var(--tp-red)' }} />

        {/* Gallery Section */}
        <div className="mb-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
            {galleryImages.map((img) => {
              const mediaType = getMediaType(img.spotifyUrl);
              const mediaConfig = mediaType ? MEDIA_CONFIG[mediaType] : null;
              const MediaIcon = mediaConfig?.Icon;
              return (
              <Dialog key={img.id}>
                <DialogTrigger asChild>
                  <div className="group relative aspect-[9/16] rounded overflow-hidden cursor-pointer border border-gray-800 hover:border-red-600 transition-all duration-300">
                    <GalleryMedia img={img} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
                      <ZoomIn size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    {img.spotifyUrl && mediaConfig && (
                      <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          openPlayer(img, mediaType);
                        }}
                        data-testid={`media-badge-${img.id}`}
                        className={`absolute top-1.5 right-1.5 w-7 h-7 rounded-full ${mediaConfig.badgeBg} flex items-center justify-center shadow-lg transition-colors z-10 tp-media-pulse cursor-pointer`}
                        title={mediaConfig.title}
                        aria-label={`${mediaConfig.title} - ${img.caption}`}
                      >
                        <MediaIcon className="w-4 h-4 fill-white" />
                      </button>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-1 sm:p-1.5 bg-gradient-to-t from-black/90 to-transparent">
                      <p className="text-white text-[9px] sm:text-[10px] font-medium truncate">{img.caption}</p>
                      <p className="text-gray-400 text-[8px] sm:text-[9px]">{img.year}</p>
                    </div>
                  </div>
                </DialogTrigger>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-[95vw] sm:max-w-2xl p-2">
                  <DialogTitle className="sr-only">{img.caption}</DialogTitle>
                  <DialogDescription className="sr-only">{img.caption} - {img.year}</DialogDescription>
                  <GalleryMedia img={img} className="w-full rounded-lg max-h-[75vh] object-contain" />
                  <div className="mt-2 pb-2 text-center space-y-2">
                    <p className="text-white text-sm sm:text-base">{img.caption} - {img.year}</p>
                    {img.spotifyUrl && mediaConfig && (
                      <button
                        type="button"
                        onClick={() => openPlayer(img, mediaType)}
                        data-testid={`media-link-${img.id}`}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${mediaConfig.buttonBg} text-white font-bold text-xs transition-colors cursor-pointer`}
                        style={{ fontFamily: 'Oswald, sans-serif' }}
                      >
                        <MediaIcon className="w-4 h-4 fill-white" />
                        {mediaConfig.label}
                      </button>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              );
            })}
          </div>
        </div>

        {/* Tabelloni Scorse Edizioni CTA */}
        <div className="mt-10 sm:mt-14 flex justify-center">
          <EditionsHistoryModal />
        </div>
      </div>

      {/* Media Player Overlay (Spotify/YouTube/Instagram) */}
      {playerMedia && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Player - ${playerMedia.caption}`}
          data-testid="wallfame-player"
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
          onClick={() => setPlayerMedia(null)}
        >
          <div
            className={`relative w-full ${playerSizeClass(playerMedia.type)} rounded-lg overflow-hidden border border-gray-700 shadow-2xl`}
            style={{ backgroundColor: '#0a0a0a' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 gap-3">
              <div className="min-w-0">
                <h6
                  className="text-sm sm:text-base font-bold text-white truncate"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  {playerMedia.caption} — {playerMedia.year}
                </h6>
                <p className="text-xs text-gray-400 capitalize">{playerMedia.type}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={playerMedia.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="wallfame-player-external"
                  aria-label="Apri esternamente"
                  title="Apri esternamente"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-800 hover:bg-gray-700 text-white transition-colors"
                >
                  <ExternalLink size={16} />
                </a>
                <button
                  type="button"
                  onClick={() => setPlayerMedia(null)}
                  data-testid="wallfame-player-close"
                  aria-label="Chiudi player"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-800 hover:bg-gray-700 text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Embed iframe */}
            <div className="relative w-full bg-black" style={playerAspect(playerMedia.type)}>
              <iframe
                src={playerMedia.embedUrl}
                title={playerMedia.caption}
                data-testid="wallfame-player-iframe"
                className="absolute top-0 left-0 w-full h-full"
                style={{ border: 0 }}
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default GalleryPage;
